import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalLoading } from 'components/ModalLoading';
import type { ContractChangeStatusEnum, ContractStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { agGridAdapter } from 'uikit';

import type { IDatasource } from 'ag-grid-community';
import { forwardRef, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AddOrEditAgreementModal } from '../AddOrEditAgreementModal';
import { useDeleteAgreementItem } from '../hooks/useDeleteAgreementItem';
import { useRegistryTableColumns } from '../hooks/useRegistryTableColumns';
import { useUploadAgreement } from '../hooks/useUploadAgreement';

import './AgreementsRegistryTable.css';

const styleOptions = agGridAdapter({
    className: 'AgreementsRegistryTable',
    size: 'm',
    borderBetweenColumns: false,
    borderBetweenRows: true,
    headerVerticalAlign: 'center',
    headerView: 'default',
    verticalAlign: 'center',
    headerHeight: 40,
});

const defaultColDef = {
    sortable: true,
    resizable: true,
};

export const AgreementsRegistryTable = forwardRef(
    (
        {
            dataSource,
            refreshDataHandler,
            refreshProject,
            currentContractId,
            changeFilterHandler,
            isLastItem,
            isDisabledByProjectStatus,
            statusContract,
            contractHasContractChanges,
            key,
        }: {
            dataSource: IDatasource;
            refreshDataHandler: () => void;
            refreshProject: () => void;
            currentContractId: number;
            changeFilterHandler: (values: any) => void;
            isLastItem: boolean;
            isDisabledByProjectStatus: boolean;
            statusContract?: ContractStatusEnum;
            contractHasContractChanges: boolean;
            key: string;
        },
        ref,
    ) => {
        const { id: projectId } = useParams();

        const { projectData } = useProjectData(projectId, false);

        const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

        const [editModalOpened, setEditModalOpened] = useState(false);
        const [currentAgreementItemId, setCurrentAgreementItemId] = useState<number | null>(null);

        const {
            confirmationDialogOpened,
            openConfirmationDialogHandler,
            submitDeleteHandler,
            cancelDeleteHandler,
            hasError,
            deleteAgreementData,
            deleteAgreementStatus,
            deleteAgreementError,
            closeErrorModalHandler,
            createErrorReportHandler,
        } = useDeleteAgreementItem({ refreshDataHandler, refreshProject, isLastItem });

        const {
            isSubmitted,
            exportContractData,
            exportContractStatus,
            exportContractError,
            handleUploadAgreement,
            handleCloseUploadAgreementError,
            handleCreateReportUploadAgreement,
            cancelCopyRequest,
            isCopying,
        } = useUploadAgreement({});

        const openEditDialogHandler = useCallback(
            (id: number, status: ContractChangeStatusEnum) => {
                setEditModalOpened(true);
                setCurrentAgreementItemId(id);

                sendGpnAnalyticsEvent({
                    componentId: 'rim_edite DS button_pressed',
                    projectId,
                    contractId: currentContractId,
                    agreementId: id,
                    agreementStatus: status,
                    projectName: projectData?.name,
                    projectType: projectData?.contractType,
                    projectDO: projectData?.subsidiary?.name,
                    methodology: projectData?.methodology,
                });
            },
            [
                currentContractId,
                projectData?.contractType,
                projectData?.methodology,
                projectData?.name,
                projectData?.subsidiary?.name,
                projectId,
                sendGpnAnalyticsEvent,
            ],
        );

        const closeEditDialogHandler = useCallback(() => {
            setEditModalOpened(false);
        }, []);

        const columnDefs = useRegistryTableColumns({
            openConfirmationDialogHandler,
            handleUploadAgreement,
            openEditDialogHandler,
            currentContractId,
            changeFilterHandler,
            isDisabledByProjectStatus,
            statusContract,
        });

        const onGridReadyHandler = useCallback(() => {
            // @ts-ignore
            if (ref?.current) {
                // @ts-ignore
                ref.current.api.sizeColumnsToFit({
                    defaultMinWidth: 100,
                });
            }
        }, [ref]);

        return (
            <div className="wrapperTableRegistry">
                <AgGridTable
                    {...styleOptions}
                    datasource={dataSource}
                    columnDefs={columnDefs}
                    overlayNoRowsTemplate="Нет данных"
                    defaultColDef={defaultColDef}
                    rowHeight={60}
                    rowModelType="infinite"
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={40}
                    cacheBlockSize={40}
                    // @ts-ignore
                    ref={ref}
                    onGridReady={onGridReadyHandler}
                    key={key}
                />
                {!contractHasContractChanges && (
                    <div className="customOverlayRegistryTable">
                        <p>
                            Для отображения данных <div>cоздайте хотя бы одно дополнительное соглашение</div>
                        </p>
                    </div>
                )}
                {editModalOpened && (
                    <AddOrEditAgreementModal
                        currentContractId={currentContractId}
                        isModalOpened={editModalOpened}
                        closeHandler={closeEditDialogHandler}
                        refreshHandler={refreshDataHandler}
                        currentAgreementItemId={currentAgreementItemId}
                    />
                )}
                {isCopying && <ModalLoading title="Выгрузка аналитики по ДС" onClose={cancelCopyRequest} />}
                <ConfirmationDialog
                    isOpened={confirmationDialogOpened}
                    onSubmit={submitDeleteHandler}
                    onClose={cancelDeleteHandler}
                    textHeader="Удаление доп. соглашения"
                    textConfirmation="Вы уверены, что хотите удалить доп. соглашение?"
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                />
                <ErrorHandlingModal
                    titleText="Удаление доп. соглашения"
                    isSubmitted={hasError}
                    statusRequest={deleteAgreementStatus}
                    dataRequest={deleteAgreementData?.uerDeleteContractChange}
                    errorsRequest={deleteAgreementError}
                    handleCloseModalError={closeErrorModalHandler}
                    handleCreateReport={createErrorReportHandler}
                />
                <ErrorHandlingModal
                    titleText="Выгрузка доп. соглашения"
                    isSubmitted={isSubmitted}
                    statusRequest={exportContractStatus}
                    dataRequest={exportContractData}
                    errorsRequest={exportContractError}
                    handleCloseModalError={handleCloseUploadAgreementError}
                    handleCreateReport={handleCreateReportUploadAgreement}
                />
            </div>
        );
    },
);
AgreementsRegistryTable.displayName = 'AgreementsRegistryTable';
